import { PageContainer } from '../../../shared/PageContainer';
import { AdditionalActionProps, useAlerts, useFeedback, ListAndDetail } from "@sis/react-library"
import ListIcon from '@mui/icons-material/List';
import { listAndDetailSamplesRoutes } from "../../ListAndDetailSamplesNavigation";
import { useState } from "react";
import { callMimicApi } from "../../../../utils/commonUtils";
import { CityTownDto } from "../../../sampleData/sampleDataReducer";
import { useSampleData } from "../../../sampleData/useSampleData";
import { Avatar } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FlagIcon from '@mui/icons-material/Flag';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import { ListAndDetailSamplesAdditionalActionsPageInfo } from './ListAndDetailSamplesAdditionalActionsPageInfo';

export const ListAndDetailSamplesAdditionalActionsPage = () => {

  const { cityTowns, deleteCityTowns } = useSampleData();

  const actionKeys = {
    download: "download",
    flag: "flag",
    multi1: "multi1",
    multi2: "multi2"
  }

  const [deleting, setDeleting] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [flagBusy, setFlagBusy] = useState(false);
  const [multi1Busy, setMulti1Busy] = useState(false);
  const [multi2Busy, setMulti2Busy] = useState(false);

  const { showSuccess, showError } = useFeedback();
  const { alert } = useAlerts();

  const handleDelete = async (cityTownsToDelete: CityTownDto[]) => {
    try {
      // In the api call, uncomment the exception in there to test errors coming from the api
      setDeleting(true);

      await callMimicApi();
      await deleteCityTowns(cityTownsToDelete);

      setDeleting(false);
      showSuccess('The countries were deleted');
    } catch (ex) {
      showError(`An error occured: ${ex}`);
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // You can add additional actions to the toolbar, you can:
  // - Put these before the new button or delete button 
  //   (use position - if position is not set it will go after the delete button).
  // - Say wether they are always available, available for one 1 item checked or for multiple checks.
  // - Change the colour of the icon.
  // - You can also use avatars.
  // - You can also add spacing between certain action icons.
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  const additionalActions: AdditionalActionProps<CityTownDto>[] = [
    {
      key: actionKeys.download, icon: <FileDownloadIcon />, busy: downloadBusy,
      tooltip: () => "Download as a document", position: "beforeNew"
    },
    { space: 20, position: "beforeDelete" },
    {
      key: actionKeys.flag, icon: <FlagIcon />, colour: "purple", busy: flagBusy,
      tooltip: () => "Flag item", position: "beforeDelete", for: "singleSelect"
    },
    { space: 20 },
    {
      key: actionKeys.multi1, icon: <FolderCopyIcon />, colour: "rgb(128,12,28)", busy: multi1Busy,
      // The tooltip can change depending on items selected
      tooltip: (items) => `Multi select 1 ${items.some((i) => i.countryCode === 'UK') ? ' - has UK items selected' : ''}`,
      for: "multiSelect"
    },
    {
      key: actionKeys.multi2, icon: <Avatar>MS</Avatar>, colour: "rgb(18,122,128)", busy: multi2Busy,
      tooltip: () => "Multi select 2", for: "multiSelect", isAvatar: true,
      // You can also add your own disable logic (ignors the for prop), uncomment below to test
      //disabled: (items) => items.some((i) => i.countryCode === 'UK'),  // Disables if UK cities selected
      //hidden: (items) => items.some((i) => i.countryCode === 'UK'),  // Hidden if UK cities selected
    }
  ];

  const handleAdditionalActionClick = async (key: string, cityTowns: CityTownDto[], clearCheckedItems: () => void) => {
    switch (key) {
      case actionKeys.download:
        setDownloadBusy(true);
        await callMimicApi();
        alert("Additional Action", "You clicked on the download button.");
        setDownloadBusy(false);
        clearCheckedItems();
        break;
      case actionKeys.flag:
        setFlagBusy(true);
        await callMimicApi();
        alert("Additional Action", `You clicked on the flag item for cityTown '${cityTowns[0].description}'`);
        setFlagBusy(false);
        clearCheckedItems();
        break;
      case actionKeys.multi1:
        setMulti1Busy(true);
        await callMimicApi();
        alert("Additional Action", `You clicked on the multi items 1 button for cityTowns [${cityTowns.map((ct) => `'${ct.description}'`)}]`);
        setMulti1Busy(false);
        clearCheckedItems();
        break;
      case actionKeys.multi2:
        setMulti2Busy(true);
        await callMimicApi();
        alert("Additional Action", `You clicked on the multi items 2 button for cityTowns [${cityTowns.map((ct) => `'${ct.description}'`)}]`);
        setMulti2Busy(false);
        clearCheckedItems();
        break;
    }
  }

  const listItemRender = (item: CityTownDto) => <>{item.description}</>;

  return (
    <PageContainer
      heading='List Samples - Additional Actions'
      icon={<ListIcon />}
      pageInfo={<ListAndDetailSamplesAdditionalActionsPageInfo />}
    >
      <ListAndDetail
        items={cityTowns}
        keyValue={(item) => item.code}
        itemRender={listItemRender}
        searchFields={['code', 'description']}
        listUrl={listAndDetailSamplesRoutes.additionalActions}
        newUrl={listAndDetailSamplesRoutes.new}
        editUrl={listAndDetailSamplesRoutes.edit}
        deleteOptions={{
          onDelete: handleDelete,
          busyDeleting: deleting
        }}
        additionalActions={{
          items: additionalActions,
          onAdditionalActionClick: handleAdditionalActionClick
        }}
      />
    </PageContainer>
  )
}
